import Udesk from 'Udesk';
import UdeskLocales from 'UdeskLocales';
import React, { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDebounceFn } from 'ahooks';
import { Button, Icon, Upload } from 'udesk-ui';
import { UploadProps } from 'udesk-ui/lib/upload';
import { postStorageCreateResource } from 'src/api/storage/createResource';
import { isNumber } from 'src/util/core';

interface TemplatePorps extends Omit<UploadProps, 'onChange'> {
    wrapperStyle?: React.CSSProperties;
    value?: any[];
    onChange?: (value: TemplatePorps['value']) => void;
    onStart?: () => void;
    onFinish?: () => void;
    isAppeal?: boolean;
    sdkOptions?: any;
}

const Template: FC<TemplatePorps> = memo((props) => {
    const {
        wrapperStyle,
        onChange,
        value,
        maxCount,
        disabled: _disabled,
        onStart,
        onFinish,
        isAppeal,
        sdkOptions: appealSdkOptions,
        ...restProps
    } = props;

    const [fileList, setFileList] = useState<any[]>([]);

    useEffect(() => {
        setFileList(value || []);
    }, [value]);

    const disabled = useMemo(() => {
        return _disabled || (isNumber(maxCount) && fileList.length >= (maxCount as number));
    }, [_disabled, fileList.length, maxCount]);

    const { run: uploadFiles } = useDebounceFn(
        (rawFiles) => {
            if (rawFiles.length) {
                onStart?.();
                Promise.all(
                    rawFiles.map(
                        (file) =>
                            new Promise((res, rej) => {
                                Udesk.app.fileStorage.upload(file, {
                                    token: new Promise((resolve, reject) => {
                                        (isAppeal
                                            ? Udesk.business.sdkAjax.get(
                                                  `open_api_v1/oss/tokens/2`,
                                                  appealSdkOptions,
                                                  { props }
                                              )
                                            : Udesk.business.getServerAuthorization({
                                                  type: 2,
                                              })
                                        ).then(
                                            function (resp) {
                                                resolve(resp);
                                            },
                                            function (reason) {
                                                reject(reason);
                                            }
                                        );
                                    }),
                                    //progressCallback: progressCallback.bind(this),
                                    successCallback: (file, a) => {
                                        const params = {
                                            keyName: a.key,
                                        };

                                        (isAppeal
                                            ? Udesk.business.sdkAjax.post(
                                                  `open_api_v1/storage/createResource`,
                                                  appealSdkOptions,
                                                  { props },
                                                  params
                                              )
                                            : postStorageCreateResource(params)
                                        ).then((resp) => {
                                            res({
                                                name: file.name,
                                                url: resp.data,
                                                status: 'success',
                                            });
                                        });
                                    },
                                    errorCallback: (file, a) => {
                                        res({
                                            name: file.name,
                                            url: a?.key,
                                            status: 'error',
                                        });
                                    },
                                });
                            })
                    )
                )
                    .then((files) => {
                        setFileList((list) => {
                            const l = [...(list || []), ...files].slice(0, maxCount);
                            onChange?.(l);
                            return l;
                        });
                    })
                    .finally(() => {
                        onFinish?.();
                    });
            }
        },
        {
            wait: 500,
        }
    );

    const beforeUpload: TemplatePorps['beforeUpload'] = useCallback(
        (file, files) => {
            uploadFiles(files);
            return false;
        },
        [uploadFiles]
    );

    const onPreview = useCallback(
        (file) => {
            const url = isAppeal
                ? Udesk.business.apiPath.concatApiPath(
                      `open_api_v1${file.url}`,
                      appealSdkOptions,
                      null,
                      true
                  )
                : file.url;
            window.open(url, '_blank');
        },
        [appealSdkOptions, isAppeal]
    );

    return (
        <div style={wrapperStyle}>
            <Upload
                fileList={fileList}
                beforeUpload={beforeUpload}
                onRemove={(file) => {
                    setFileList((list) => {
                        const l = list.filter((i) => i.uid !== file.uid);
                        onChange?.(l);
                        return l;
                    });
                }}
                onPreview={onPreview}
                {...restProps}
            >
                <Button disabled={disabled} icon={<Icon type="UploadOutlined" antdIcon={true} />}>
                    {/* 上传文件 */}
                    {
                        UdeskLocales['current'].components.qualityScore.components.uplaod.index
                            .uploadFile
                    }
                </Button>
            </Upload>
        </div>
    );
});

export default Template;
